function subscriptionBuilderIsTapcartWebview() {
  return (
    typeof window.Tapcart !== 'undefined' ||
    document.documentElement.classList.contains('tapcart-webbridge') ||
    document.body.classList.contains('tapcart-webbridge')
  );
}

function subscriptionBuilderApplyTapcartBottomInset(host) {
  if (!subscriptionBuilderIsTapcartWebview()) return;
  host.dataset.tapcartWebview = 'true';
  const frequencyPanel = document.querySelector('subscription-builder-frequency-panel');
  if (frequencyPanel) frequencyPanel.dataset.tapcartWebview = 'true';
}

class SubscriptionBuilder extends HTMLElement {
  constructor() {
    super();

    this.builderType = 'new'; // or 'edit'
    this.bundle = [];
    this.finalPrice = 0;
    this.fullPrice = 0;
    this.sellingPlanGroup = {};
    this.selectedSellingPlan = {};
    this.selectedSellingPlanId = null;
    this.rechargeSession = null;
    this.clubMembershipToggleOn = false;
    this.footer = null;
    this.isApp = false;
    this.activeClubMember = false;
    this.bundleToEdit = [];
    this.currentEditingSubscription = null;
    this.subVersion = 'v3';
    this.subscriptionProducts = window.__abc?.subscriptionProducts || [];
    this.clubProductInCart = false;
    this.isPdpClubToggleOn = false;
    this.onetimes = [];
    this.bindEventHandlers();
  }

  connectedCallback() {
    this.initializeSellingPlan();
    this.setupEventListeners();
    this.initSubscriptionBuilderLinks();
    this.initRecharge();
    this.activeClubMember = this.getAttribute('active-club-member') === 'true';
    this.isApp = this.getAttribute('is-app') === 'true';
    if (this.isApp) {
      this.open(null);
    }
    this.footer = this.querySelector('subscription-builder-footer');
    // console.log('this.activeClubMember', this.activeClubMember);
    if (this.activeClubMember) {
      this.clubMembershipToggleOn = true;
      this.updateFooter();
      this.updateProductCards();
      this.updateProgressBar();
    }
    subscriptionBuilderApplyTapcartBottomInset(this);
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  bindEventHandlers() {
    [
      'handleAddItemToBundle',
      'handleRemoveItemFromBundle',
      'handleFrequencyChanged',
      'handleCloseSubscriptionBuilder',
      'handleMembershipToggle',
      'handleOverlayClick',
      'handleOpenFrequencyPanel',
    ].forEach((handler) => {
      if (this[handler]) {
        this[handler] = this[handler].bind(this);
      }
    });
  }

  async initRecharge() {
    if (typeof recharge === 'undefined') {
      console.error('Recharge library is not loaded');
      return;
    }
  }

  initializeSellingPlan() {
    try {
      this.sellingPlanGroup = JSON.parse(this.getAttribute('selling-plan-group'));
    } catch (e) {
      console.error('Invalid JSON for selling-plan-group attribute:', e);
    }

    if (this.sellingPlanGroup && this.sellingPlanGroup.selling_plans) {
      this.selectedSellingPlanId = parseInt(this.getAttribute('selected-selling-plan-id'), 10);

      if (this.selectedSellingPlanId) {
        this.selectedSellingPlan = this.sellingPlanGroup.selling_plans.find(
          (plan) => plan.id === this.selectedSellingPlanId
        );
      } else {
        this.selectedSellingPlan = this.sellingPlanGroup.selling_plans[0];
        this.selectedSellingPlanId = this.selectedSellingPlan?.id;
      }
    } else {
      console.error('Selling plan group is not properly defined.');
    }
  }

  setupEventListeners() {
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#SubscriptionBuilder-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.handleOverlayClick);
    }
    const frequencyToggle = this.querySelector('#frequency-selection-toggle-open');
    if (frequencyToggle) {
      frequencyToggle.addEventListener('click', this.handleOpenFrequencyPanel);
    }

    this.addEventListener('add_item_to_bundle', this.handleAddItemToBundle);
    this.addEventListener('remove_item_from_bundle', this.handleRemoveItemFromBundle);
    this.addEventListener('frequencyChanged', this.handleFrequencyChanged);
    this.addEventListener('closeSubscriptionBuilder', this.handleCloseSubscriptionBuilder);
    this.addEventListener(
      'subscription-builder-toggle-club-membership',
      this.handleMembershipToggle
    );
  }

  removeEventListeners() {
    this.removeEventListener('add_item_to_bundle', this.handleAddItemToBundle);
    this.removeEventListener('remove_item_from_bundle', this.handleRemoveItemFromBundle);
    this.removeEventListener('frequencyChanged', this.handleFrequencyChanged);
    this.removeEventListener('closeSubscriptionBuilder', this.handleCloseSubscriptionBuilder);
    this.removeEventListener(
      'subscription-builder-toggle-club-membership',
      this.handleMembershipToggle
    );

    const overlay = this.querySelector('#SubscriptionBuilder-Overlay');
    if (overlay) {
      overlay.removeEventListener('click', this.handleOverlayClick);
    }
    const frequencyToggle = this.querySelector('#frequency-selection-toggle-open');
    if (frequencyToggle) {
      frequencyToggle.removeEventListener('click', this.handleOpenFrequencyPanel);
    }
  }

  handleOverlayClick() {
    this.close();
  }

  handleOpenFrequencyPanel(event) {
    event.preventDefault();
    const frequencyPanel = document.querySelector('subscription-builder-frequency-panel');
    frequencyPanel?.open(event.currentTarget, this.selectedSellingPlanId);
  }

  handleCloseSubscriptionBuilder() {
    this.close();
  }
  handleMembershipToggle() {
    this.clubMembershipToggleOn = !this.clubMembershipToggleOn;
    this.updateFooter();
    this.updateProductCards();
    this.updateProgressBar();
  }

  initSubscriptionBuilderLinks() {
    const subscriptionLinks = document.querySelectorAll('a[href="#open_subscription_builder"]');
    subscriptionLinks.forEach((subscriptionLink) => {
      subscriptionLink.setAttribute('role', 'button');
      subscriptionLink.setAttribute('aria-haspopup', 'dialog');
      subscriptionLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(subscriptionLink);
      });
      subscriptionLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(subscriptionLink);
        }
      });
    });
  }

  open(
    triggeredBy,
    subscriptionKey = null,
    builderType = null,
    startingProduct = null,
    clubMembershipToggleOn = false,
    onetimes = null,
    startingBundle = null,
    frequencyPanel = false
  ) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (clubMembershipToggleOn) {
      this.isPdpClubToggleOn = clubMembershipToggleOn;
    }
    this.bundle = [];
    if (subscriptionKey && typeof subscriptionKey === 'object') {
      if (subscriptionKey.external_variant_id.ecommerce == '42121503866961') {
        this.subVersion = 'v3a';
      }
      if (onetimes) {
        this.onetimes = onetimes;
      }
      this.handleEditActiveSubscription(subscriptionKey);
    } else if (typeof subscriptionKey === 'string') {
      this.handleEditSubscriptionByKey(subscriptionKey);
    } else if (Array.isArray(startingBundle) && startingBundle.length > 0) {
      this.addProductsToBundle(startingBundle);
    } else if (startingProduct) {
      const product = this.subscriptionProducts.find((p) => p.id == startingProduct);
      if (product) {
        this.handleAddItemToBundle({ detail: { product } });
      }
    }
    if (!triggeredBy) {
      this.classList.add('active');
    } else {
      this.classList.add('animate', 'active');
    }
    this.updateFooter();
    setTimeout(() => {
      this.querySelector('subscription-builder-progress-bar')?.render();
    }, 300);

    if (frequencyPanel) {
      const freqToggle = this.querySelector('#frequency-selection-toggle-open');
      freqToggle?.click();
    }

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('SubscriptionBuilder');
        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  handleEditActiveSubscription(subscriptionKey) {
    /*
   console.log(
      'Handling subscription key as an object for editActiveSubscription:',
      subscriptionKey
    );
    console.log('this.onetimes', this.onetimes);
    */

    this.builderType = 'editActiveSubscription';
    this.currentEditingSubscription = subscriptionKey;
    const subscriptionBuilderClubUpsellEl = this.querySelector('.subscription_builder_club_upsell');
    if (subscriptionBuilderClubUpsellEl) {
      subscriptionBuilderClubUpsellEl.style.display = 'none';
    }
    const subscriptionBuilderFrequencyBarWrapperEl = this.querySelector(
      '.subscription-builder-frequency-bar-wrapper'
    );
    if (subscriptionBuilderFrequencyBarWrapperEl) {
      subscriptionBuilderFrequencyBarWrapperEl.style.display = 'none';
    }

    const productsToEdit = subscriptionKey.include.bundle_selections.items;
    this.addProductsToBundle(productsToEdit);
  }

  handleEditSubscriptionByKey(subscriptionKey) {
    this.builderType = 'edit';
    const productsToEdit = window.__abc.ShopifyCartData.cart.items.filter(
      (lineItem) => lineItem.properties?._rc_bundle == subscriptionKey
    );
    this.bundleToEdit = productsToEdit;

    if (productsToEdit.length > 0) {
      this.addProductsToBundle(productsToEdit);
    }
  }

  addProductsToBundle(products) {
    products.forEach((item) => {
      for (let i = 0; i < item.quantity; i++) {
        const product = this.subscriptionProducts.find(
          (product) => product.id == (item.external_product_id || item.product_id)
        );
        if (product) {
          this.handleAddItemToBundle({ detail: { product } });
        }
      }
    });
  }

  close() {
    if (this.isApp) {
      return true;
    }
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  handleAddItemToBundle(event) {
    const { product } = event.detail;
    this.bundle.push(product);

    this.updateProgressBar();
    this.updateProductCardQuantity(product.id);
  }

  addProductsToBundle(products) {
    products.forEach((item) => {
      const qty = Number(item.quantity || 1);
      const pid = String(item.external_product_id || item.product_id);

      for (let i = 0; i < qty; i++) {
        const product = this.subscriptionProducts.find((p) => String(p.id) == pid);
        if (product) {
          this.handleAddItemToBundle({ detail: { product } });
        }
      }
    });
  }

  handleFrequencyChanged(event) {
    const { selectedSellingPlanId, selectedPlanText } = event.detail;
    this.selectedSellingPlanId = selectedSellingPlanId;
    //.subscription-builder-frequency-bar-wrapper #selected_selling_plan_name
    const selectedSellingPlanNameEl = this.querySelector('#selected_selling_plan_name');
    if (selectedSellingPlanNameEl) {
      selectedSellingPlanNameEl.innerText = selectedPlanText;
    }
    this.updateFooter();
  }

  handleRemoveItemFromBundle(event) {
    const { product } = event.detail;
    const productIndex = this.bundle.findIndex((item) => item.id === product.id);
    if (productIndex > -1) {
      this.bundle.splice(productIndex, 1);
    }
    this.updateProductCardQuantity(product.id);
    this.updateProgressBar();
  }

  updateProgressBar() {
    const progressBar = this.querySelector('subscription-builder-progress-bar');
    //this.isPdpClubToggleOn

    const showClubPricing = Boolean(this.clubMembershipToggleOn || this.isPdpClubToggleOn);

    const bundleSize = Array.isArray(this.bundle) ? this.bundle.length : 0;

    const oneTimeCount = this.onetimes.reduce((acc, item) => {
      acc += item.quantity;
      return acc;
    }, 0);
    let totalBundleSize = bundleSize;
    if (this.builderType == 'editActiveSubscription' && showClubPricing) {
      totalBundleSize = oneTimeCount + bundleSize;
    }

    if (progressBar) {
      progressBar.clubMembershipToggle = showClubPricing;
      progressBar.setAttribute('bundle-size', totalBundleSize.toString());
      progressBar.render();
    }
    this.updateProductCards();
  }

  updateProductCardQuantity(productId) {
    const productCards = this.querySelectorAll('subscription-builder-product-card');
    const productQtyInBundle = this.bundle.filter((product) => product.id === productId).length;
    productCards.forEach((card) => {
      if (card.getAttribute('product-id') == productId) {
        card.setAttribute('in-bundle', productQtyInBundle);
      }
    });
    this.updateFooter();
  }

  updateFooter() {
    if (!this.footer) {
      this.footer = this.querySelector('subscription-builder-footer');
    }
    // If it still isn’t in the DOM, nothing to do yet:
    if (!this.footer) return;

    const payload = {
      bundle: this.bundle,
      selectedSellingPlanId: this.selectedSellingPlanId,
      clubMembershipToggleOn: this.clubMembershipToggleOn,
      builderType: this.builderType,
      bundleToEdit: this.bundleToEdit,
      currentEditingSubscription: this.currentEditingSubscription,
      subVersion: this.subVersion,
      isPdpClubToggleOn: this.isPdpClubToggleOn,
    };

    // <subscription-builder-footer>'s defining script (subscription-builder-footer.js)
    // loads after this one. For club members this.updateFooter() runs synchronously
    // from connectedCallback() the moment this element upgrades — which can happen
    // before the footer's script has registered its class, leaving `this.footer` an
    // un-upgraded element with no updateProperties method. Wait for it instead of throwing.
    if (typeof this.footer.updateProperties === 'function') {
      this.footer.updateProperties(payload);
    } else {
      customElements.whenDefined('subscription-builder-footer').then(() => {
        this.footer?.updateProperties?.(payload);
      });
    }
  }

  updateProductCards() {
    const productCards = this.querySelectorAll('subscription-builder-product-card');
    const showClubPricing = Boolean(this.clubMembershipToggleOn || this.isPdpClubToggleOn);
    const bundleSize = Array.isArray(this.bundle) ? this.bundle.length : 0;
    const oneTimeCount = this.onetimes.reduce((acc, item) => {
      acc += item.quantity;
      return acc;
    }, 0);
    let totalBundleSize = bundleSize;
    // console.log(this.builderType);
    if (this.builderType == 'editActiveSubscription' && showClubPricing) {
      totalBundleSize = oneTimeCount + bundleSize;
    }
    productCards.forEach((card) => {
      card.totalBundleSize = totalBundleSize;
      card.clubMembershipToggleOn = showClubPricing;
    });
  }
}

customElements.define('subscription-builder', SubscriptionBuilder);
